import {useElementSize, Show, Space, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {FocusEvent, useId, useState} from 'react';

import {isNotNaN, isNotNil, isNumber} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {KEYBOARD_KEYS} from '../../constants/keyboardKeys';
import {FormControlProps} from '../../types/FormControlProps';
import {DropdownItem} from '../Dropdown/components/DropdownItem';
import {Dropdown} from '../Dropdown/Dropdown';
import {HelperText} from '../HelperText/HelperText';
import {IconButton} from '../IconButton/IconButton';
import {Label} from '../Label/Label';
import {PlatformNumberInputElement} from '../NumberInput/components/PlatformNumberInputElement';
import {PlatformInputWrapper} from '../TextInput/components/PlatformInputWrapper';

export interface NumberInputWithOptionsProps
  extends FormControlProps<number | null, HTMLInputElement, [string | null]>,
    TestIdProps {
  /**
   * @about
   * The number of allowed decimal places
   */
  decimalPlaces?: number;
  options: number[];
  openOnFocus?: boolean;
  placeholder?: string;
  isRecommended?: boolean;
  isClearable?: boolean;
  isFormatNumbersDisabled?: boolean;
}

export function NumberInputWithOptions(props: NumberInputWithOptionsProps) {
  const formatNumber = useFormatNumber();

  const id = useId();
  const dropdownId = useId();

  const [inputValue, setInputValue] = useState<string | Nullish>();
  const [isOpen, open, close] = useBoolean(false);
  const [isFocused, setFocusedTrue, setFocusedFalse] = useBoolean(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);

  const [ref, width] = useElementSize<HTMLDivElement>();

  const handleOptionSelect = (value: number) => {
    props.onChange?.(value, undefined, String(value));
    close();
    setFocusedOptionIndex(-1);
  };

  const handleInputFocus = (event: FocusEvent<HTMLInputElement, Element>) => {
    setFocusedTrue();
    props.onFocus?.(event);
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
    setFocusedFalse();
    props.onBlur?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    match(event.key)
      .with(KEYBOARD_KEYS.DOWN, () => {
        event.preventDefault();
        if (!isOpen) {
          open();
          setFocusedOptionIndex(0);
        } else {
          const nextIndex = Math.min(focusedOptionIndex + 1, props.options.length - 1);
          setFocusedOptionIndex(nextIndex);
        }
      })
      .with(KEYBOARD_KEYS.UP, () => {
        event.preventDefault();
        if (isOpen) {
          const prevIndex = Math.max(focusedOptionIndex - 1, 0);
          setFocusedOptionIndex(prevIndex);
        }
      })
      .with(KEYBOARD_KEYS.HOME, () => {
        event.preventDefault();
        if (isOpen) {
          setFocusedOptionIndex(0);
        }
      })
      .with(KEYBOARD_KEYS.END, () => {
        event.preventDefault();
        if (isOpen) {
          setFocusedOptionIndex(props.options.length - 1);
        }
      })
      .otherwise(() => {});
  };

  const isInvalid = props.isInvalid || !!props.errorMessage;
  const isDropdownOpen = props.openOnFocus ? isFocused : isOpen;
  const describedBy = props.errorMessage || props.helperText ? `${id}-helper` : '';

  return (
    <VStack>
      <Label
        id={id}
        isRequired={props.isRequired}
        isRecommended={props.isRecommended}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <Dropdown
        isOpen={props.isDisabled ? false : isDropdownOpen}
        onClose={() => {
          close();
          setFocusedOptionIndex(-1);
          // Focus the input element when the dropdown is closed
          const inputElement = document.getElementById(id);
          inputElement?.focus();
        }}
        placement="bottom"
        isHeightLimited
        isFocusManagementDisabled
        dropdownControl={
          <PlatformInputWrapper
            isDisabled={props.isDisabled}
            isInvalid={isInvalid}
            ref={ref}
            isRecommended={props.isRecommended}
          >
            <PlatformNumberInputElement
              errorMessage={props.errorMessage}
              helperText={props.helperText}
              isDisabled={props.isDisabled}
              isRequired={props.isRequired}
              label={props.label}
              name={props.name}
              onChange={props.onChange}
              placeholder={props.placeholder}
              tooltip={props.tooltip}
              value={props.value}
              decimalPlaces={props.decimalPlaces}
              id={id}
              isInvalid={isInvalid}
              inputValue={inputValue}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              setInputValue={setInputValue}
              isFormatNumbersDisabled={props.isFormatNumbersDisabled}
              data-testid={props['data-testid']}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-expanded={isDropdownOpen}
              aria-controls={dropdownId}
              aria-describedby={describedBy}
              aria-autocomplete="list"
              aria-haspopup="listbox"
            />
            <Space horizontal={2} />
            <Show when={props.isClearable && isNotNil(props.value) && isNotNaN(props.value)}>
              <IconButton
                onClick={() => {
                  props.onChange?.(null, undefined, null);
                }}
                size="small"
                icon="navigation/close"
                isDisabled={props.isDisabled}
                tabIndex={-1}
                aria-label="Clear value"
                data-testid={suffixTestId('clearableButton', props)}
              />
            </Show>
            <IconButton
              onClick={() => (isOpen ? close() : open())}
              size="small"
              icon={isOpen ? 'navigation/expand_less' : 'navigation/expand_more'}
              isDisabled={props.isDisabled}
              tabIndex={-1}
              aria-label={isOpen ? 'Close options' : 'Open options'}
              aria-controls={dropdownId}
              data-testid={suffixTestId('expandButton', props)}
            />
          </PlatformInputWrapper>
        }
        data-testid={suffixTestId('dropdown', props)}
      >
        <div
          css={css`
            min-width: ${isNumber(width) ? `${width}px` : 'initial'};
          `}
          role="listbox"
          id={dropdownId}
          aria-label={`${props.label || 'Number'} options`}
        >
          {props.options.map((option, index) => (
            <DropdownItem
              key={option}
              label={
                (props.isFormatNumbersDisabled ? option?.toString() : formatNumber(option)) ?? ''
              }
              onClick={() => handleOptionSelect(option)}
              data-testid={suffixTestId(`option-${index}`, props)}
              isActive={focusedOptionIndex === index}
            />
          ))}
        </div>
      </Dropdown>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
